import React from 'react';
import FileList, { DownloadLink, PreviousVersions } from './FileList';
import { fmtBytes } from '../workflow/constants';

// Read-only view of an engagement's attachments, shared by the workflow detail
// pages (Engagement Amendment, Signed Financial Statements) so the "Engagement attachments" panel
// looks the same everywhere. Only the CURRENT version of each document shows;
// superseded / older versions collapse into "Previous versions". Labels are the
// document type (no legacy "CW/CC" prefix). See docs/modules/engagements.md §6.
// Rows are printed with shared/FileList, same as every other file list.

const DOC_TYPE_LABELS = {
  QC6: 'QC6', QC7: 'QC7', QC7_PREV: 'Previous year QC',
  GROUP_STRUCTURE: 'Group structure', FS: 'Signed Financial Statements', QC35: 'QC35 Form', SUPPLEMENTARY: 'Supplementary',
  INDEPENDENCE_CONFIRMATION: 'Independence Confirmation',
  // The Registration's BizFile — it reaches this panel through
  // the engreg aggregation, already scoped to this engagement's own company.
  BIZFILE: 'BizFile',
  // The screening document — the requester's own (Registration answered No,
  // source engreg) or QM's, read from the screening record (source screening);
  // an engagement-owned upload supersedes either.
  SCREENING: 'Screening',
};

const badgeFor = (f) => {
  // Legacy wf_fs rows can carry a qc35 doc (pre-split) — label by docType.
  if (f.source === 'fs') return f.docType === 'qc35' ? 'QC35 Form' : 'Signed Financial Statements';
  if (f.source === 'qc35') return f.docType === 'screenshot' ? 'CaseWare screenshot' : 'QC35 Form';
  if (f.source === 'group') return 'Group structure';
  if (f.source === 'prev') return 'Previous year QC';
  // Generated on download from the engagement itself.
  if (f.source === 'indep') return 'Independence Confirmation';
  return DOC_TYPE_LABELS[f.docType] || f.docType || 'QC6/7';
};

/**
 * @param {Array} attachments - rows from GET .../attachments (carry superseded)
 * @param {(f) => string} [downloadHref] - builds the per-file download URL;
 *   omitted = list only, no download (Client Hub)
 */
const EngagementAttachments = ({ attachments = [], downloadHref }) => {
  if (attachments.length === 0) {
    return <p className="text-sm text-slate-500">No engagement documents.</p>;
  }
  const current = attachments.filter((f) => !f.superseded);
  const previous = attachments.filter((f) => f.superseded);

  const list = (files, muted) => (
    <FileList
      files={files}
      rowKey={(f) => `${f.source}-${f.fileId}`}
      className={muted ? 'mt-1' : ''}
      row={(f) => ({
        name: f.originalName,
        muted,
        badges: [[badgeFor(f), 'bg-slate-100 text-slate-600']],
        meta: [f.requestNo, fmtBytes(f.sizeBytes)],
        actions: downloadHref ? <DownloadLink href={downloadHref(f)} /> : null,
      })}
    />
  );

  return (
    <>
      {current.length > 0 && list(current, false)}
      <PreviousVersions count={previous.length}>{list(previous, true)}</PreviousVersions>
    </>
  );
};

export default EngagementAttachments;
